"use client";

import { useState, useTransition } from "react";
import Image from "next/image";
import { toast } from "sonner";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Pencil,
  PencilLine,
  Trash2,
} from "lucide-react";
import ProblemAnswer from "@/components/ProblemAnswer";
import ProblemContentRenderer from "@/components/ProblemContentRenderer";
import PostVoteButtons from "@/components/community/restoration/PostVoteButtons";
import RestorationCommentSection from "@/components/community/restoration/RestorationCommentSection";
import PostEditor from "@/app/community/restoration/PostEditor";
import PostAdminControls from "@/app/community/restoration/PostAdminControls";
import { blocksToProblemString } from "@/lib/restorationBlocks";
import type { RestorationExamSession, RestorationPost } from "@/lib/server-actions/restoration/_shared";
import { deletePostAsAdmin } from "@/lib/server-actions/restoration/posts";

interface PostCardProps {
  post: RestorationPost;
  myVote: "up" | "down" | null;
  currentUserId: string | null;
  isAdmin: boolean;
  sessions: RestorationExamSession[];
  /** 펼쳐진 상태로 고정해 렌더할지 (1위 슬롯 등) */
  initialExpanded?: boolean;
  /** 펼치기 버튼을 완전히 숨길지 (1위 슬롯 고정 렌더) */
  alwaysExpanded?: boolean;
  /** 투표 등 부분 필드 로컬 업데이트 */
  onPostChanged: (
    postId: number,
    next: Partial<RestorationPost> | null
  ) => void;
  /** 수정 완료 등 서버 상태 전체 리페치가 필요할 때 */
  onRefreshNeeded: () => void;
}

/**
 * JCG-1569: 복원 게시물 카드.
 * - `alwaysExpanded`: 1위 슬롯처럼 항상 펼친 상태로 렌더 (토글 버튼 숨김)
 * - 펼친 상태: ProblemAnswer + preamble/closing + 투표 + 인라인 댓글
 * - 본인 글: 수정 버튼 → 인라인 PostEditor(edit mode) 교체
 * - 관리자: 삭제 버튼 (본인 글 삭제 기능은 spec상 없음)
 */
export default function PostCard({
  post,
  myVote,
  currentUserId,
  isAdmin,
  sessions,
  initialExpanded = false,
  alwaysExpanded = false,
  onPostChanged,
  onRefreshNeeded,
}: PostCardProps) {
  const [expanded, setExpanded] = useState(initialExpanded || alwaysExpanded);
  const [editing, setEditing] = useState(false);
  const [isPending, startTransition] = useTransition();

  const isOwner = !!currentUserId && post.clerk_user_id === currentUserId;
  const canEdit = isAdmin || (isOwner && !post.is_confirmed);
  const problemString = blocksToProblemString(post.body.blocks);

  const handleDelete = () => {
    if (!isAdmin) return;
    if (
      !window.confirm(
        "정말 이 복원 게시물을 삭제하시겠습니까? 되돌릴 수 없습니다."
      )
    ) {
      return;
    }
    startTransition(async () => {
      const result = await deletePostAsAdmin(post.id);
      if (result.success) {
        toast.success("게시물이 삭제되었습니다.");
        onPostChanged(post.id, null);
      } else {
        toast.error(result.error || "삭제에 실패했습니다.");
      }
    });
  };

  if (editing) {
    return (
      <div className="rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-neutral-900/40 p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
            게시물 수정 중
          </span>
        </div>
        <PostEditor
          sessions={sessions}
          isAdmin={isAdmin}
          editingPost={post}
          adminEditing={isAdmin && !isOwner}
          onSuccess={() => {
            setEditing(false);
            onRefreshNeeded();
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <article className="rounded-md border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900/40 p-4">
      <header className="flex items-center mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-300">
          {post.profile_image_url && (
            <Image
              src={post.profile_image_url}
              alt={post.author ?? "작성자"}
              width={24}
              height={24}
              className="rounded-full"
            />
          )}
          <span>{post.author ?? "익명"}</span>
          {post.post_status === "confirmed" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 px-2 py-0.5 text-xs font-medium">
              <CheckCircle2 className="w-3 h-3" />
              확정
            </span>
          )}
          {post.post_status === "editing" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 px-2 py-0.5 text-xs font-medium">
              <PencilLine className="w-3 h-3" />
              종합중
            </span>
          )}
          <time
            className="text-xs text-gray-400 dark:text-neutral-500"
            dateTime={post.created_at}
          >
            {new Date(post.created_at).toLocaleDateString("ko-KR", {
              month: "short",
              day: "numeric",
            })}
          </time>
        </div>
      </header>

      {!expanded && (
        <div className="flex flex-col gap-3">
          {post.preamble_text && (
            <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-neutral-300">
              {post.preamble_text}
            </p>
          )}
          {problemString ? (
            <ProblemContentRenderer
              content={problemString}
              componentId={`restoration-post-collapsed-${post.id}`}
            />
          ) : (
            <p className="text-sm text-gray-400 italic">(미리보기 없음)</p>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="shrink-0 inline-flex items-center gap-1 rounded-md border border-gray-200 dark:border-neutral-700 px-3 py-1 text-xs text-gray-600 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800"
            >
              <ChevronDown className="w-3.5 h-3.5" />
              펼치기
            </button>
          </div>
        </div>
      )}

      {expanded && (
        <>
          {post.preamble_text && (
            <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-neutral-300 mb-3">
              {post.preamble_text}
            </p>
          )}

          {problemString && (
            <ProblemAnswer
              id={`restoration-post-${post.id}`}
              problem={problemString}
              correctAnswer={post.body.correctAnswer ?? ""}
              choices={post.body.choices}
              disableHistory
              alwaysShowChoices
              defaultSolutionOpen
            />
          )}

          {post.closing_text && (
            <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-neutral-300 mt-3">
              {post.closing_text}
            </p>
          )}

          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 dark:border-neutral-700 px-2 py-1 text-xs text-gray-600 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800"
                >
                  <Pencil className="w-3 h-3" />
                  수정
                </button>
              )}
              {isAdmin && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isPending}
                  className="inline-flex items-center gap-1 rounded-md border border-rose-200 dark:border-rose-900 px-2 py-1 text-xs text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 disabled:opacity-50"
                >
                  <Trash2 className="w-3 h-3" />
                  삭제
                </button>
              )}
            </div>
            {!alwaysExpanded && (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 dark:border-neutral-700 px-2 py-1 text-xs text-gray-600 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800"
              >
                <ChevronUp className="w-3 h-3" />
                접기
              </button>
            )}
          </div>

          {isAdmin && (
            <PostAdminControls
              post={post}
              onPostChanged={onPostChanged}
              onRefreshNeeded={onRefreshNeeded}
            />
          )}

          <div className="mt-6 mb-2 flex flex-col items-center gap-2">
            <p className="text-xs sm:text-sm text-gray-500 dark:text-neutral-400">
              이 복원이 실제 시험 문제와 같다고 생각하시나요?
            </p>
            <PostVoteButtons
              postId={post.id}
              upCount={post.up_count}
              downCount={post.down_count}
              myVote={myVote}
              onChange={(next) =>
                onPostChanged(post.id, {
                  up_count: next.up,
                  down_count: next.down,
                })
              }
              size="lg"
              className="justify-center"
            />
          </div>

          <RestorationCommentSection postId={post.id} />
        </>
      )}
    </article>
  );
}
